import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../services/firebase/config';
import { 
  getUserDetails, 
  updatePhoneNumber, 
  updateBillingInfo, 
  updatePaymentMethod 
} from '../../services/firebase/accountService';
import { signOut } from 'firebase/auth';
import { 
  UserCircle, 
  Phone, 
  CreditCard, 
  Building2,
  Edit2,
  Plus
} from 'lucide-react';

const AccountDetail = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState({
    phone: false,
    billing: false,
    payment: false
  });
  const [formData, setFormData] = useState({
    phone: '',
    billingAddress: '',
    cardNumber: '',
    cardHolder: '',
    expiry: '',
    cvv: ''
  });

  const loadUserData = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const details = await getUserDetails(user.email);
      setUserDetails(details);
      // Form verilerini mevcut kullanıcı bilgileriyle doldur
      setFormData(prev => ({
        ...prev,
        phone: details?.phone || '',
        billingAddress: details?.billingAddress || '',
        cardNumber: details?.paymentMethod?.cardNumber || '',
        cardHolder: details?.paymentMethod?.cardHolder || '',
        expiry: details?.paymentMethod?.expiry || ''
      }));
    } catch (error) {
      console.error('Error loading user data:', error);
      setError('Kullanıcı bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      setError('Çıkış yapılırken bir hata oluştu');
    }
  };

  const handleEdit = (field) => {
    setEditMode(prev => ({ ...prev, [field]: true }));
  };

  const handleCancel = (field) => {
    setEditMode(prev => ({ ...prev, [field]: false }));
    loadUserData();
  };

  // Form submit handlers
  const handleUpdate = async (type) => {
    try {
      setLoading(true);
      switch(type) {
        case 'phone':
          await updatePhoneNumber(user.email, formData.phone);
          break;
        case 'billing':
          await updateBillingInfo(user.email, formData.billingAddress);
          break;
        case 'payment':
          await updatePaymentMethod(user.email, {
            cardNumber: formData.cardNumber,
            cardHolder: formData.cardHolder,
            expiry: formData.expiry
          });
          break;
        default:
          break;
      }
      setEditMode(prev => ({ ...prev, [type]: false }));
      await loadUserData(); // Verileri yeniden yükle
    } catch (error) {
      console.error(`Error updating ${type}:`, error);
      setError(`${type} bilgileri güncellenirken hata oluştu`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-4">Yükleniyor...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Hesap Ayarları</h1>
        <button
          onClick={handleLogout}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Çıkış yap
        </button>
      </div>

      <div className="bg-white dark:bg-[#1A1D1F] rounded-lg p-6 mb-6 shadow">
        <div className="flex items-center mb-6">
          {userDetails?.photoURL ? (
            <img src={userDetails.photoURL} alt={userDetails.displayName} className="w-16 h-16 rounded-full mr-4" />
          ) : (
            <UserCircle className="w-16 h-16 text-gray-400 mr-4" />
          )}
          <div>
            <h2 className="text-xl text-gray-900 dark:text-white">{userDetails?.displayName}</h2>
            <span className="text-gray-500 dark:text-gray-400">{userDetails?.email}</span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-gray-50 dark:bg-[#272B30] rounded">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center space-x-3">
                <Phone className="text-gray-400" />
                <span className="text-gray-700 dark:text-gray-200">Telefon Numarası</span>
              </div>
              {!editMode.phone && (
                <button 
                  onClick={() => handleEdit('phone')}
                  className="text-blue-500 hover:text-blue-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {editMode.phone ? (
              <div className="space-y-2">
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="(5XX) XXX XX XX"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleCancel('phone')}
                    className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-800"
                  >
                    İptal
                  </button>
                  <button
                    onClick={() => handleUpdate('phone')}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                {userDetails?.phone || 'Telefon numarası eklenmemiş'}
              </p>
            )}
          </div>

          <div className="p-4 bg-gray-50 dark:bg-[#272B30] rounded">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center space-x-3">
                <Building2 className="text-gray-400" />
                <span className="text-gray-700 dark:text-gray-200">Fatura Adresi</span>
              </div>
              {!editMode.billing && (
                <button 
                  onClick={() => handleEdit('billing')}
                  className="text-blue-500 hover:text-blue-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {editMode.billing ? (
              <div className="space-y-2">
                <textarea
                  value={formData.billingAddress}
                  onChange={(e) => setFormData(prev => ({ ...prev, billingAddress: e.target.value }))}
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  rows={3}
                  placeholder="Fatura adresinizi girin"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleCancel('billing')}
                    className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-800"
                  >
                    İptal
                  </button>
                  <button
                    onClick={() => handleUpdate('billing')}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">
                {userDetails?.billingInfo?.address || 'Fatura adresi eklenmemiş'}
              </p>
            )}
          </div>

          <div className="p-4 bg-gray-50 dark:bg-[#272B30] rounded">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center space-x-3">
                <CreditCard className="text-gray-400" />
                <span className="text-gray-700 dark:text-gray-200">Ödeme Yöntemi</span>
              </div>
              {!editMode.payment && (
                <button 
                  onClick={() => handleEdit('payment')}
                  className="text-blue-500 hover:text-blue-600"
                >
                  {userDetails?.paymentMethod ? (
                    <Edit2 className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
            {editMode.payment ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={formData.cardHolder}
                  onChange={(e) => setFormData(prev => ({ ...prev, cardHolder: e.target.value }))}
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-2"
                  placeholder="Kart Üzerindeki İsim"
                />
                <input
                  type="text"
                  value={formData.cardNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, cardNumber: e.target.value }))}
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-2"
                  placeholder="Kart Numarası"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={formData.expiry}
                    onChange={(e) => setFormData(prev => ({ ...prev, expiry: e.target.value }))}
                    className="p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="MM/YY"
                  />
                  <input
                    type="text"
                    value={formData.cvv}
                    onChange={(e) => setFormData(prev => ({ ...prev, cvv: e.target.value }))}
                    className="p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="CVV"
                  />
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    onClick={() => handleCancel('payment')}
                    className="px-3 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-800"
                  >
                    İptal
                  </button>
                  <button
                    onClick={() => handleUpdate('payment')}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {userDetails?.paymentMethod ? (
                  <div className="space-y-1">
                    <p className="text-gray-600 dark:text-gray-400">
                      {userDetails.paymentMethod.cardHolder}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      **** **** **** {userDetails.paymentMethod.lastFourDigits}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      Son Kullanma: {userDetails.paymentMethod.expiry}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">
                    Kayıtlı kart bilgisi bulunmuyor
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-[#1A1D1F] rounded-lg p-6 shadow">
        <h2 className="text-xl text-gray-900 dark:text-white mb-4">PRO Üyelik</h2>
        {userDetails?.membershipStatus === 'active' ? (
          <div className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-4 rounded">
            <p>PRO üyeliğiniz aktif durumda</p>
            {userDetails.membershipEndDate && (
              <p className="text-gray-600 dark:text-gray-300">
                Bitiş Tarihi: {new Date(userDetails.membershipEndDate).toLocaleDateString('tr-TR')}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-[#272B30] p-4 rounded">
            <p className="text-gray-700 dark:text-gray-200 mb-4">
              Detaylı analizler, webinarlar ve çok daha fazlası için PRO'ya yükseltin.
            </p>
            <button 
              onClick={() => navigate('/payment')}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              PRO'ya Yükselt
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded">
          {error}
        </div>
      )}
    </div>
  );
};

export default AccountDetail;